import React, { createContext, useState, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Axios, calculateBoxes, clearAllRedirect, getNextFifteen, getSid, getViewportWidth } from "./utils";
import { baseApi, sizes } from "./constants";
import type { AppContext, Config, ConfigApiResponse, ContextValue, Size } from "../types";

const configTransform = (v: Config): Config => JSON.parse(JSON.stringify(v).replace(/\|URL_PREFIX\|/g, `/account/`));

export const Context = createContext<ContextValue>({} as ContextValue);
const iframe = window.self !== window.top;

const calcSize = (): Size => {
    let output: Size = `large`;
    const vw = getViewportWidth();
    sizes.forEach(([min, max, nm]) => {
        if (vw > min && vw < max) {
            output = nm;
        }
    });
    return output;
};

const calcStoreCellsAcross = () => {
    const caw = Math.min(1440, getViewportWidth());
    const sca = calculateBoxes(caw, calcSize());
    return { sca, caw };
};

let redrawing: ReturnType<typeof setTimeout> | null | false = null;
const resize = (
    setSize: React.Dispatch<React.SetStateAction<Size>>,
    setLastResized: React.Dispatch<React.SetStateAction<number>>,
    setStoreCellsAcross: React.Dispatch<React.SetStateAction<number>>,
    setContentAreaWidth: React.Dispatch<React.SetStateAction<number>>,
    setViewportWidth: React.Dispatch<React.SetStateAction<number>>
) => {
    clearTimeout(redrawing as ReturnType<typeof setTimeout>);
    redrawing = setTimeout(() => {
        window.requestAnimationFrame(() => {
            clearTimeout(redrawing as ReturnType<typeof setTimeout>);
            redrawing = false;
            setLastResized(Date.now());
            const cellParams = calcStoreCellsAcross();
            setStoreCellsAcross(cellParams.sca);
            setContentAreaWidth(cellParams.caw);
            setSize(calcSize());
            setViewportWidth(getViewportWidth());
        });
    }, 55);
};

export const ContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [searchParams] = useSearchParams();
    const providedSubProgram = sessionStorage.getItem(`sp`) ?? searchParams.get(`sp`);
    if (providedSubProgram) {
        sessionStorage.setItem(`sp`, providedSubProgram);
    }
    const adminConfigs = localStorage.getItem(`adminConfig`)
        ? JSON.parse(localStorage.getItem(`adminConfig`) as string)
        : {};
    const adminConfig = adminConfigs[providedSubProgram || `base`];
    const config = localStorage.getItem(`config`) ? JSON.parse(localStorage.getItem(`config`) as string) : {};
    const [context, sc] = useState<AppContext>({
        user: {},
        adminConfig,
        config,
        programGuid: localStorage.getItem(`programGuid`) ?? undefined,
    });
    const [loaded, setLoaded] = useState(false);
    const [axiosIsReady, setAxiosIsReady] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(getViewportWidth());
    const [loading, setLoading] = useState(false);
    const [size, setSize] = useState<Size>(`large`);
    const [storeCellsAcross, setStoreCellsAcross] = useState(5);
    const [contentAreaWidth, setContentAreaWidth] = useState(1400);
    const [lastResized, setLastResized] = useState(Date.now());
    const [Lastadminconfigupdate, setLastadminconfigupdate] = useState<string | undefined>();
    const setContext = (nv: Partial<Config>) => sc({ ...context, config: { ...context.config, ...nv } });
    const getValue = (key?: string) => (key ? context?.[key as keyof AppContext] : context);
    const location = useLocation();

    const refreshContext = (): Promise<void> =>
        Axios.get<ConfigApiResponse>(
            `${baseApi}config?host=${btoa(window.location.host)}${iframe ? `&iframe=1` : ``}${
                providedSubProgram ? `&sp=${providedSubProgram}` : ``
            }`
        )
            .then(r => {
                const { data } = r;
                const liBxInstalledData = sessionStorage.getItem(`liBxInstalledData`);
                data.config = configTransform(data.config);
                sc({
                    ...context,
                    ...data,
                    liBxInstalledData,
                    nextFifteen: getNextFifteen(
                        data?.holidays.map((d: string) => new Date(d)),
                        data.adminConfig
                    ),
                    ...(data.getMyGiftOptions ? { getMyGiftOptions: data.getMyGiftOptions } : {}),
                });

                if (getSid()) {
                    setLoaded(true);
                }
                if (data.adminConfig) {
                    const allConfigs = localStorage.getItem(`adminConfig`)
                        ? JSON.parse(localStorage.getItem(`adminConfig`) as string)
                        : {};
                    allConfigs[providedSubProgram || `base`] = data.adminConfig;
                    localStorage.setItem(`adminConfig`, JSON.stringify(allConfigs));
                }
                if (data.config) {
                    localStorage.setItem(`config`, JSON.stringify(data.config));
                }
                if (data.programGuid) {
                    localStorage.setItem(`programGuid`, data.programGuid);
                }
                if (data.post) {
                    window.parent.postMessage({ action: `signedIn`, resp: data.post }, `*`);
                }
                if (data.sp) {
                    sessionStorage.setItem(`sp`, data.sp);
                }
            })
            .catch(() => {
                toast.error(`Sorry something has gone wrong`);
            });

    useEffect(() => {
        Axios.interceptors.response.use(
            // Any status code that lie within the range of 2xx cause this function to trigger
            // Do something with response data
            response => {
                if (response?.headers?.pragma && Lastadminconfigupdate !== response.headers.pragma) {
                    if (Lastadminconfigupdate) {
                        refreshContext();
                    }
                    setLastadminconfigupdate(response.headers.pragma);
                }
                if (response.headers?.liauthorization) {
                    if (sessionStorage.getItem(`sid`)) {
                        sessionStorage.setItem(`sid`, response.headers.liauthorization);
                    } else {
                        localStorage.setItem(`sid`, response.headers.liauthorization);
                    }
                }
                return response;
            },
            error => {
                // Any status codes that falls outside the range of 2xx cause this function to trigger
                // Do something with response error
                if (error.response && [401, 502].indexOf(error.response.status) > -1) {
                    clearAllRedirect(context);
                }
                return Promise.reject(error);
            }
        );
        Axios.interceptors.request.use(
            x => {
                const r = x;
                r.headers.Authorization = `Bearer ${getSid() || `  `}`;

                return r;
            },
            error => Promise.reject(error)
        );
        setAxiosIsReady(true);
        resize(setSize, setLastResized, setStoreCellsAcross, setContentAreaWidth, setViewportWidth);
        window.addEventListener(`resize`, () =>
            resize(setSize, setLastResized, setStoreCellsAcross, setContentAreaWidth, setViewportWidth)
        );
    }, []);

    useEffect(() => {
        if (context?.adminConfig?.mall?.Favicon) {
            const icons = document.getElementsByTagName(`link`);
            if (icons) {
                const ar = Array.prototype.slice.call(icons) as HTMLLinkElement[];
                for (let i = 0; i < ar.length; i += 1) {
                    if (ar[i].getAttribute(`data-litag`)) {
                        return;
                    }
                }
                const lnk = document.createElement(`link`);
                lnk.setAttribute(`data-litag`, `favicon`);
                lnk.rel = `icon`;
                lnk.href = context?.adminConfig?.mall?.Favicon;
                document.head.appendChild(lnk);
            }
        }
    }, [context]);

    useEffect(() => {
        const sid = getSid();
        if (!loading && axiosIsReady) {
            refreshContext();
            if (sid) {
                setLoading(true);
            }
        }
    }, [location.pathname, axiosIsReady]);

    const save = (): Promise<Config> => Axios.put(`${baseApi}config`, context.config).then(r => r.data as Config);

    const contextMemo = useMemo(
        () => ({
            setContext,
            save,
            user: context.user,
            context,

            contentAreaWidth,
            editable: !!sessionStorage.getItem(`edit`),
            viewportWidth,
            Axios,
            loaded,
            getValue,
            size,
            storeCellsAcross,
            refreshContext,
            useMobileTables: viewportWidth < 1150,
        }),
        [context, lastResized, axiosIsReady]
    );

    return <Context.Provider value={contextMemo}>{children}</Context.Provider>;
};
